import React, { useEffect, useState } from "react";
import { FaCheck, FaStore, FaCalendarDay, FaClock } from "react-icons/fa6";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { FaMapMarkerAlt, FaShoppingBag } from "react-icons/fa";

const SuccessfulPayment = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { loginData } = useSelector((state) => state.Authentication);

  const [orderDetails, setOrderDetails] = useState(null);
  const [loading, setLoading] = useState(true);

  // Get order data from navigation state
  useEffect(() => {
    console.log("📍 Location state:", location.state);

    if (location.state) {
      console.log("📦 Received order data:", location.state);
      setOrderDetails(location.state);
    } else {
      console.log("❌ No order data received");
      // Try to get from sessionStorage as fallback
      const storedOrder = sessionStorage.getItem("lastOrder");
      if (storedOrder) {
        setOrderDetails(JSON.parse(storedOrder));
      }
    }
    setLoading(false);
  }, [location.state]);

  // Store order data in sessionStorage as backup
  useEffect(() => {
    if (orderDetails) {
      sessionStorage.setItem("lastOrder", JSON.stringify(orderDetails));
    }
  }, [orderDetails]);

  // Prevent back navigation to payment page
  useEffect(() => {
    const handlePopState = (event) => {
      event.preventDefault();
      navigate("/", { replace: true });
    };

    window.addEventListener("popstate", handlePopState);
    window.history.pushState(null, "", window.location.href);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [navigate]);

  // Format address from the received data
  const formatAddress = (shippingAddress) => {
    if (!shippingAddress) return "Address not available";
    return [
      shippingAddress.street,
      shippingAddress.area,
      shippingAddress.city,
      shippingAddress.pincode,
    ]
      .filter(Boolean)
      .join(", ");
  };

  // Generate fallback order data
  const getFallbackOrderData = () => {
    return {
      orderId: `ORD-${Date.now()}`,
      orderType: "unknown",
      orderData: {
        items: [],
        totalAmount: 0,
        totalSavings: 0,
        stCoinsUsed: 0,
        paymentMethod: "Cash on Delivery",
        orderDate: new Date().toLocaleDateString(),
        estimatedDelivery: "2-3 business days",
        shippingAddress: {
          name: loginData?.Name || "Customer",
          phone: loginData?.Mobile || "Not provided",
        },
      },
    };
  };

  if (loading) {
    return (
      <div className="min-vh-100 bg-light d-flex align-items-center justify-content-center">
        <div className="text-center">
          <div className="spinner-border text-primary mb-3"></div>
          <p>Loading order details...</p>
        </div>
      </div>
    );
  }

  // Use fallback data if no order details
  const displayOrder = orderDetails || getFallbackOrderData();
  const { orderId, orderType, orderData } = displayOrder;

  console.log("📊 Display order data:", displayOrder);

  return (
    <div className="min-vh-100 bg-light" style={{marginTop:"30px"}}>
      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-lg-8">
            {/* Success Icon Section */}
            <div className="text-center mb-5">
              <div
                className="bg-success rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                style={{ width: "120px", height: "120px" }}
              >
                <FaCheck style={{ fontSize: "60px", color: "#fff" }} />
              </div>
              <h3 className="fw-bold text-dark mb-2">
                {orderDetails
                  ? "Payment Successful"
                  : "Order Placed Successfully!"}
              </h3>
              <p className="text-muted mb-4">
                {orderDetails
                  ? "Thank you for your order. We'll send you a confirmation email with your order details shortly."
                  : "Your order has been confirmed. You will receive order details shortly."}
              </p>
              {!orderDetails && (
                <div className="alert alert-warning d-inline-block">
                  <small>
                    Order details loading delayed. Please check your email for
                    confirmation.
                  </small>
                </div>
              )}
            </div>

            {/* Order Summary Card */}
            <div className="bg-white rounded-4 p-4 mb-4 border-0 shadow-sm">
              <h5 className="fw-bold mb-4 text-dark d-flex align-items-center">
                <FaShoppingBag className="me-2" style={{ color: "#1469A7" }} />
                Order Summary
              </h5>

              <div className="row">
                <div className="col-md-6 mb-3">
                  <div className="d-flex align-items-center mb-2">
                    <span className="text-muted fw-medium me-2">Order ID:</span>
                    <span className="fw-bold text-dark">{orderId}</span>
                  </div>
                  <div className="d-flex align-items-center mb-2">
                    <span className="text-muted fw-medium me-2">
                      Order Date:
                    </span>
                    <span className="fw-bold text-dark">
                      {orderData?.orderDate || new Date().toLocaleDateString()}
                    </span>
                  </div>
                  <div className="d-flex align-items-center">
                    <span className="text-muted fw-medium me-2">
                      Order Type:
                    </span>
                    <span className="fw-bold text-dark text-capitalize">
                      {orderType}
                    </span>
                  </div>
                </div>
                <div className="col-md-6 mb-3">
                  <div className="d-flex align-items-center mb-2">
                    <span className="text-muted fw-medium me-2">
                      Total Amount:
                    </span>
                    <span className="fw-bold fs-5" style={{ color: "#1469A7" }}>
                      ₹{orderData?.totalAmount || 0}
                    </span>
                  </div>
                  <div className="d-flex align-items-center mb-2">
                    <span className="text-muted fw-medium me-2">
                      Payment Method:
                    </span>
                    <span className="fw-bold text-dark">
                      {orderData?.paymentMethod || "Cash on Delivery"}
                    </span>
                  </div>
                </div>
              </div>

              {/* ST Coins Used */}
              {orderData?.stCoinsUsed > 0 && (
                <div className="mt-3 pt-3 border-top">
                  <div className="d-flex align-items-center">
                    <span className="text-muted fw-medium me-2">
                      ST Coins Used:
                    </span>
                    <span className="fw-bold text-success">
                      {orderData.stCoinsUsed} coins (₹{orderData.stCoinsUsed}{" "}
                      off)
                    </span>
                  </div>
                </div>
              )}

              {/* Total Savings */}
              {orderData?.totalSavings > 0 && (
                <div className="mt-2">
                  <div className="d-flex align-items-center">
                    <span className="text-muted fw-medium me-2">
                      You Saved:
                    </span>
                    <span className="fw-bold text-success">
                      ₹{orderData.totalSavings}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Order Items - Only show if items exist */}
            {orderData?.items && orderData.items.length > 0 && (
              <div className="bg-white rounded-4 p-4 mb-4 border-0 shadow-sm">
                <h6 className="fw-bold mb-3 text-dark">Order Items</h6>
                {orderData.items.map((item, index) => (
                  <div key={index} className="d-flex border-bottom pb-3 mb-3">
                    <img
                      src={item.ImgUrl || "https://via.placeholder.com/80"}
                      alt={item.ProductName}
                      className="rounded-3 me-3"
                      style={{
                        width: "80px",
                        height: "80px",
                        objectFit: "cover",
                      }}
                    />
                    <div className="flex-grow-1">
                      <h6 className="fw-bold mb-1 text-dark">
                        {item.ProductName}
                      </h6>
                      <p className="text-muted small mb-2">{item.Brand}</p>

                      {item.SalonName && (
                        <div
                          className="d-flex align-items-center bg-light rounded px-2 py-1 mb-2"
                          style={{ width: "fit-content" }}
                        >
                          <FaStore
                            size={12}
                            className="me-1"
                            style={{ color: "#1469A7" }}
                          />
                          <span
                            className="small fw-semibold"
                            style={{ color: "#1469A7" }}
                          >
                            {item.SalonName}
                          </span>
                        </div>
                      )}

                      <div className="d-flex justify-content-between align-items-center">
                        <span className="text-muted">Qty: {item.Qty || 1}</span>
                        <span className="fw-bold text-dark">
                          ₹{item.Price || item.TotalAmount}
                        </span>
                      </div>

                      {/* Services for salon orders */}
                      {item.Services && (
                        <div className="mt-2">
                          {item.Services.map((service, serviceIndex) => (
                            <div
                              key={serviceIndex}
                              className="d-flex align-items-start mb-1"
                            >
                              <FaCheck
                                size={10}
                                className="text-success mt-1 me-2"
                              />
                              <span className="small text-muted">
                                {service.service}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Shipping Address */}
            <div className="bg-white rounded-4 p-4 mb-4 border-0 shadow-sm">
              <h6 className="fw-bold mb-3 text-dark d-flex align-items-center">
                <FaMapMarkerAlt className="me-2" style={{ color: "#1469A7" }} />
                Shipping Address
              </h6>
              <div className="bg-light rounded-3 p-3">
                <h6 className="fw-bold text-dark mb-2">
                  {orderData?.shippingAddress?.name ||
                    loginData?.Name ||
                    "Customer"}
                </h6>
                <p className="text-muted mb-2">
                  {formatAddress(orderData?.shippingAddress)}
                </p>
                <p className="text-muted mb-0">
                  Phone:{" "}
                  {orderData?.shippingAddress?.phone ||
                    loginData?.Mobile ||
                    "Not provided"}
                </p>
              </div>
            </div>

            {/* Schedule Details (for salon orders) */}
            {orderData?.serviceDate && (
              <div className="bg-white rounded-4 p-4 mb-4 border-0 shadow-sm">
                <h6 className="fw-bold mb-3 text-dark">Service Schedule</h6>
                <div className="d-flex justify-content-between">
                  <div className="d-flex align-items-center flex-grow-1">
                    <div
                      className="bg-light rounded-circle d-flex align-items-center justify-content-center me-3"
                      style={{ width: "40px", height: "40px" }}
                    >
                      <FaCalendarDay size={16} style={{ color: "#1469A7" }} />
                    </div>
                    <div>
                      <p className="small text-muted mb-1 fw-medium">
                        Service Date
                      </p>
                      <p className="fw-bold text-dark mb-0">
                        {orderData.serviceDate}
                      </p>
                    </div>
                  </div>

                  <div
                    className="border-start mx-3"
                    style={{ height: "50px" }}
                  ></div>

                  <div className="d-flex align-items-center flex-grow-1">
                    <div
                      className="bg-light rounded-circle d-flex align-items-center justify-content-center me-3"
                      style={{ width: "40px", height: "40px" }}
                    >
                      <FaClock size={16} style={{ color: "#1469A7" }} />
                    </div>
                    <div>
                      <p className="small text-muted mb-1 fw-medium">
                        Service Time
                      </p>
                      <p className="fw-bold text-dark mb-0">
                        {orderData.serviceTime}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="text-center mt-5">
              <button
                className="btn btn-outline-primary btn-lg px-4 me-3"
                onClick={() => navigate("/")}
                style={{ borderColor: "#1469A7", color: "#1469A7" }}
              >
                Continue Shopping
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuccessfulPayment;
